/**
 * Der Sync-Dienst der App (T-04): startet beim Laden, gleicht ab und haelt
 * den Status fuer Kopfzeile und Einstellungen.
 *
 * Abgeglichen wird beim Start, bei Rueckkehr in den Tab, wenn das Geraet
 * wieder online ist, alle zwei Minuten bei sichtbarem Tab und drei Sekunden
 * nach einer lokalen Aenderung. Nie zwei Abgleiche gleichzeitig.
 */
import { create } from 'zustand';

import { db } from '@/db/schema';
import { abgleichen, META, offeneAnzahl, type SyncKontext } from '@/lib/sync/abgleich';
import { SyncApi, SyncFehler, type FehlerArt } from '@/lib/sync/api';
import { abmelden, einrichten, serverZuruecksetzen, syncStarten, type Modus, type SyncKonfig } from '@/lib/sync/einrichtung';

export type SyncZustand = 'aus' | 'bereit' | 'laeuft' | 'offline' | 'fehler';

interface SyncStore {
  zustand: SyncZustand;
  konfig: SyncKonfig | null;
  zuletzt: string | null;
  fehler: string | null;
  fehlerArt: FehlerArt | null;
  offen: number;
}

export const useSync = create<SyncStore>(() => ({
  zustand: 'aus', konfig: null, zuletzt: null, fehler: null, fehlerArt: null, offen: 0,
}));

const INTERVALL = 2 * 60_000;
const NACH_AENDERUNG = 3_000;

let kontext: SyncKontext | null = null;
let laeuft: Promise<void> | null = null;
let nochmal = false;
let verzoegert: ReturnType<typeof setTimeout> | null = null;
let takt: ReturnType<typeof setInterval> | null = null;

const offenAktualisieren = async () => useSync.setState({ offen: await offeneAnzahl(db) });

async function einmal(): Promise<void> {
  if (!kontext) return;
  useSync.setState({ zustand: 'laeuft' });
  try {
    const r = await abgleichen(kontext);
    useSync.setState({ zustand: 'bereit', zuletzt: r.zeit, fehler: null, fehlerArt: null });
  } catch (e) {
    const f = e instanceof SyncFehler ? e : new SyncFehler('server', e instanceof Error ? e.message : String(e));
    useSync.setState({ zustand: f.art === 'offline' ? 'offline' : 'fehler', fehler: f.message, fehlerArt: f.art });
    if (f.art !== 'offline') console.warn('Synchronisierung:', f);
  } finally {
    await offenAktualisieren();
  }
}

/** Jetzt abgleichen. Laeuft schon einer, folgt direkt danach ein weiterer. */
export function jetztAbgleichen(): Promise<void> {
  if (laeuft) {
    nochmal = true;
    return laeuft;
  }
  laeuft = (async () => {
    do {
      nochmal = false;
      await einmal();
    } while (nochmal);
  })().finally(() => { laeuft = null; });
  return laeuft;
}

function nachAenderung(): void {
  if (verzoegert) clearTimeout(verzoegert);
  verzoegert = setTimeout(() => { verzoegert = null; void jetztAbgleichen(); }, NACH_AENDERUNG);
  void offenAktualisieren();
}

const beiSichtbar = () => { if (document.visibilityState === 'visible') void jetztAbgleichen(); };
const beiOnline = () => void jetztAbgleichen();

function ausloeserAn(): void {
  db.erfassung.nachAenderung = nachAenderung;
  document.addEventListener('visibilitychange', beiSichtbar);
  window.addEventListener('online', beiOnline);
  takt = setInterval(() => { if (document.visibilityState === 'visible') void jetztAbgleichen(); }, INTERVALL);
}

function ausloeserAus(): void {
  db.erfassung.nachAenderung = null;
  document.removeEventListener('visibilitychange', beiSichtbar);
  window.removeEventListener('online', beiOnline);
  if (takt) clearInterval(takt);
  if (verzoegert) clearTimeout(verzoegert);
  takt = verzoegert = null;
}

/** Beim App-Start einmal aufrufen. Ohne Einrichtung passiert nichts. */
export async function syncInitialisieren(): Promise<void> {
  const e = await syncStarten(db);
  if (!e) return;
  kontext = e.kontext;
  const zuletzt = (await db.meta.get(META.zuletzt))?.value;
  useSync.setState({ zustand: 'bereit', konfig: e.konfig, zuletzt: typeof zuletzt === 'string' ? zuletzt : null });
  ausloeserAn();
  void jetztAbgleichen();
}

export async function syncEinrichten(passwort: string, geraetName: string, modus: Modus): Promise<void> {
  const e = await einrichten(db, new SyncApi(), passwort, geraetName, modus);
  kontext = e.kontext;
  useSync.setState({ zustand: 'bereit', konfig: e.konfig, fehler: null, fehlerArt: null });
  ausloeserAus();
  ausloeserAn();
  await jetztAbgleichen();
}

async function beenden(aktion: () => Promise<void>): Promise<void> {
  ausloeserAus();
  if (laeuft) await laeuft;
  await aktion();
  kontext = null;
  useSync.setState({ zustand: 'aus', konfig: null, zuletzt: null, fehler: null, fehlerArt: null, offen: 0 });
}

export const syncAbmelden = () => beenden(() => abmelden(db));
export const syncServerZuruecksetzen = () => beenden(() => serverZuruecksetzen(db, kontext?.api ?? new SyncApi()));

/** Ist der Server erreichbar, und gibt es dort schon eine Kopie? */
export async function serverPruefen(): Promise<'leer' | 'vorhanden' | 'nicht_verfuegbar' | 'login' | 'offline'> {
  try {
    return (await new SyncApi().schluesselHolen()) ? 'vorhanden' : 'leer';
  } catch (e) {
    const art = e instanceof SyncFehler ? e.art : 'nicht_verfuegbar';
    return art === 'login' || art === 'offline' ? art : 'nicht_verfuegbar';
  }
}
